import type { MapView } from "./event-state";

export type WindowKey = "1h" | "6h" | "24h" | "48h" | "7d";

/** First load and empty hash. Covers a quiet polar gap without jumping to 7 d. */
export const DEFAULT_WINDOW: WindowKey = "48h";
/** First load and empty hash. Major events — the public map, not every suspected look. */
export const DEFAULT_VIEW: MapView = "campaigns";
export type PlaceKey = "be" | "ib" | "eu";
/** @deprecated attention is a MapView now; kept so old `#f=` hashes still parse. */
export type StatFilter = "hot" | "new" | "imp";
export type EventMode = MapView;

export type Permalink = {
  window?: WindowKey;
  id?: string;
  place?: PlaceKey;
  /** Exclusive map chip. Default major events — omit from the hash. */
  view?: MapView;
  /** Legacy `#e=1` sheet flag — the app redirects this to `/events`. */
  events?: boolean;
  /** Map / chart playhead in UTC ms. Omitted when the clock is LIVE. */
  t?: number;
};

export const WINDOW_MS: Record<WindowKey, number> = {
  "1h": 3600_000,
  "6h": 6 * 3600_000,
  "24h": 24 * 3600_000,
  "48h": 48 * 3600_000,
  "7d": 7 * 24 * 3600_000,
};

const WINDOW_ORDER: WindowKey[] = ["1h", "6h", "24h", "48h", "7d"];
const WINDOWS = new Set<WindowKey>(WINDOW_ORDER);
const PLACES = new Set<PlaceKey>(["be", "ib", "eu"]);

/** Smallest live window that still contains t, or null if older than 7 d. */
export function windowContainingPlayhead(tMs: number, untilMs: number): WindowKey | null {
  if (!Number.isFinite(tMs) || !Number.isFinite(untilMs)) return null;
  const age = untilMs - tMs;
  if (age < 0) return DEFAULT_WINDOW;
  for (const key of WINDOW_ORDER) {
    if (age <= WINDOW_MS[key]) return key;
  }
  return null;
}

/** Window for a shared link: honour w= unless it cannot hold t. */
export function bootWindow(boot: Permalink, nowMs = Date.now()): WindowKey {
  if (boot.t == null) return boot.window ?? DEFAULT_WINDOW;
  const needed = windowContainingPlayhead(boot.t, nowMs);
  if (!needed) return boot.window ?? DEFAULT_WINDOW;
  if (!boot.window) return needed;
  if (WINDOW_MS[boot.window] < WINDOW_MS[needed]) return needed;
  return boot.window;
}

/** `2026-08-16T1427Z` — no colons, so the hash stays readable. */
export function formatPermalinkTime(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}${p(d.getUTCMinutes())}Z`;
}

export function parsePermalinkTime(raw: string): number | undefined {
  const s = raw.trim();
  if (!s) return undefined;
  const compact = /^(\d{4}-\d{2}-\d{2})T(\d{2})(\d{2})Z$/i.exec(s);
  if (compact) {
    const t = Date.parse(`${compact[1]}T${compact[2]}:${compact[3]}:00Z`);
    return Number.isFinite(t) ? t : undefined;
  }
  const t = Date.parse(s.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);
  return Number.isFinite(t) ? t : undefined;
}

/** Camera the event link should land on. Only the places the map already has. */
export function placeKeyForCountry(country: string): PlaceKey | undefined {
  if (country === "BE") return "be";
  if (country === "ES" || country === "PT") return "ib";
  return undefined;
}

function parseView(hash: URLSearchParams): MapView | undefined {
  const f = hash.get("f");
  if (f === "new") return "new";
  if (f === "hot") return "rising";
  if (f === "imp") return "campaigns";
  const m = hash.get("m");
  if (m === "suspected" || m === "watching") return "suspected";
  if (m === "fires" || m === "observed" || m === "likely" || m === "all") return "fires";
  if (m === "new" || m === "rising" || m === "campaigns" || m === "fine") return m;
  return undefined;
}

/** `#w=24h&id=inc_abc&p=ib&m=suspected` — one view, never mode AND filter. */
export function parsePermalink(hash: string): Permalink {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return {};
  const q = new URLSearchParams(raw);
  const out: Permalink = {};
  const w = q.get("w");
  if (w && WINDOWS.has(w as WindowKey)) out.window = w as WindowKey;
  const id = (q.get("id") ?? "").trim();
  if (id && /^inc_[a-z0-9]+$/i.test(id)) out.id = id;
  const p = q.get("p");
  if (p && PLACES.has(p as PlaceKey)) out.place = p as PlaceKey;
  const view = parseView(q);
  if (view) out.view = view;
  if (q.get("e") === "1") out.events = true;
  const t = parsePermalinkTime(q.get("t") ?? "");
  if (t != null) out.t = t;
  return out;
}

export function formatPermalink(state: Permalink): string {
  const q = new URLSearchParams();
  if (state.window && state.window !== DEFAULT_WINDOW) q.set("w", state.window);
  // Empty hash means "ask for location / show Europe", not Belgium.
  if (state.place && state.place !== "eu") q.set("p", state.place);
  // Default (major events) stays off the hash. `#f=imp` still parses.
  if (state.view && state.view !== DEFAULT_VIEW) {
    if (state.view === "new") q.set("f", "new");
    else if (state.view === "rising") q.set("f", "hot");
    else if (state.view === "campaigns") q.set("f", "imp");
    else q.set("m", state.view);
  }
  if (state.id) q.set("id", state.id);
  if (state.t != null && Number.isFinite(state.t)) q.set("t", formatPermalinkTime(state.t));
  if (state.events) q.set("e", "1");
  const s = q.toString();
  return s ? `#${s}` : "";
}
